import React, { useEffect, useRef, useState } from "react";
import { MapPin, Crosshair, Check } from "lucide-react";

/**
 * Interactive pin-drop map. The pin stays visually pinned to the center of the map;
 * as the user pans the map, the center coordinate updates. On `moveend`, we emit the
 * new center so the parent can re-quote the delivery fee in near real-time.
 */
export default function PinDropMap({
  initialCenter,        // { lat, lng } — where the map opens
  restaurant,           // { lat, lng } — reference marker (non-draggable)
  onChange,             // (center: { lat, lng }) => void  — called on pan end
  height = 300,
}) {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const restoMarkerRef = useRef(null);
  const [center, setCenter] = useState(initialCenter || restaurant || { lat: 25.8944, lng: 93.7253 });
  const [locating, setLocating] = useState(false);

  useEffect(() => {
    const L = window.L;
    if (!L || !ref.current) return;
    const start = initialCenter || restaurant || { lat: 25.8944, lng: 93.7253 };
    if (!mapRef.current) {
      mapRef.current = L.map(ref.current, { zoomControl: false, attributionControl: false }).setView([start.lat, start.lng], 15);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19 }).addTo(mapRef.current);
      L.control.zoom({ position: "bottomright" }).addTo(mapRef.current);

      // Restaurant marker (stays fixed)
      if (restaurant) {
        const ricon = L.divIcon({
          className: "",
          html: `<div class="mj-marker resto">M</div>`,
          iconSize: [36, 36], iconAnchor: [18, 18],
        });
        restoMarkerRef.current = L.marker([restaurant.lat, restaurant.lng], { icon: ricon, interactive: false }).addTo(mapRef.current);
      }

      const emit = () => {
        const c = mapRef.current.getCenter();
        const next = { lat: +c.lat.toFixed(6), lng: +c.lng.toFixed(6) };
        setCenter(next);
        onChange?.(next);
      };
      mapRef.current.on("moveend", emit);
      // Initial emit so parent gets a quote right away
      setTimeout(emit, 50);
    }
    // eslint-disable-next-line
  }, []);

  // If parent swaps `initialCenter` (e.g. via geolocation) programmatically, recenter
  useEffect(() => {
    if (!mapRef.current || !initialCenter) return;
    mapRef.current.setView([initialCenter.lat, initialCenter.lng], mapRef.current.getZoom() < 15 ? 15 : mapRef.current.getZoom());
  }, [initialCenter?.lat, initialCenter?.lng]);

  const recenterToMe = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (p) => {
        mapRef.current?.setView([p.coords.latitude, p.coords.longitude], 16);
        setLocating(false);
      },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  return (
    <div className="relative rounded-2xl overflow-hidden border border-gray-200 bg-gray-50" style={{ height }} data-testid="pindrop-map-wrap">
      <div ref={ref} className="mj-map absolute inset-0" />

      {/* Center pin overlay — stays visually fixed as map moves */}
      <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
        <div className="relative -translate-y-4">
          <MapPin className="w-10 h-10 text-[#C85A5A] drop-shadow-[0_4px_8px_rgba(0,0,0,0.4)] fill-[#C85A5A]/30" strokeWidth={2.5} />
          <div className="absolute left-1/2 bottom-0 translate-x-[-50%] w-2 h-2 rounded-full bg-black/60" />
        </div>
      </div>

      {/* Recenter-to-me button */}
      <button
        type="button"
        onClick={recenterToMe}
        disabled={locating}
        className="absolute top-3 right-3 bg-white rounded-full p-2 shadow-[0_4px_14px_rgba(0,0,0,0.12)] hover:bg-gray-50 active:scale-95 transition disabled:opacity-60"
        title="Use my current location"
        data-testid="pindrop-recenter"
      >
        <Crosshair className={`w-4 h-4 ${locating ? "animate-spin" : ""}`} />
      </button>

      {/* Current coords readout */}
      <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur rounded-xl px-3 py-1.5 shadow text-[11px] font-mono text-gray-600" data-testid="pindrop-coords">
        {center.lat.toFixed(5)}, {center.lng.toFixed(5)}
      </div>
    </div>
  );
}
